import React, { useState, useEffect } from 'react';
import { Zap, AlertTriangle, Info, CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Scope2 = ({ onSubmit, initialData = {} }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    useMarketBased: true,
    sites: [],
    marketBasedEvidence: {},
    assumptions: [],
    ...initialData
  });

  // Load initial data when component mounts or initialData changes
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData]);

  // Auto-save to localStorage whenever formData changes
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      try {
        const currentData = JSON.parse(localStorage.getItem('calculationWorkflowData') || '{}');
        const updatedData = {
          ...currentData,
          scope2: formData
        };
        localStorage.setItem('calculationWorkflowData', JSON.stringify(updatedData));
      } catch (error) {
        console.error('Error saving Scope2 data to localStorage:', error);
      }
    }
  }, [formData]);

  const [errors] = useState({});
  const [warnings] = useState({});

  // Sample data - in real app this would come from data_freeze_v1
  const sampleSites = React.useMemo(() => [
    {
      id: 1,
      name: 'Headquarters',
      country: 'US',
      region: 'California',
      consumption: 50000,
      unit: 'kWh',
      locationBasedEF: 0.312,
      locationBasedEmissions: 15.6,
      marketBasedEF: 0.150,
      marketBasedEmissions: 7.5,
      supplierSpecific: {
        enabled: true,
        ef: 0.150,
        contractId: 'CON-2024-001',
        contractUrl: 'https://example.com/contract/001',
        volumesCovered: 40000,
        period: '2024-01-01 to 2024-12-31'
      },
      residualMix: {
        enabled: false,
        ef: 0.450,
        volume: 0
      }
    },
    {
      id: 2,
      name: 'Manufacturing Plant',
      country: 'DE',
      region: 'Bavaria',
      consumption: 120000,
      unit: 'kWh',
      locationBasedEF: 0.485,
      locationBasedEmissions: 58.2,
      marketBasedEF: 0.280,
      marketBasedEmissions: 33.6,
      supplierSpecific: {
        enabled: false,
        ef: null,
        contractId: '',
        contractUrl: '',
        volumesCovered: 0,
        period: ''
      },
      residualMix: {
        enabled: true,
        ef: 0.280,
        volume: 120000
      }
    },
    {
      id: 3,
      name: 'Warehouse',
      country: 'PL',
      region: 'Mazovia',
      consumption: 25000,
      unit: 'kWh',
      locationBasedEF: 0.756,
      locationBasedEmissions: 18.9,
      marketBasedEF: 0.756,
      marketBasedEmissions: 18.9,
      supplierSpecific: {
        enabled: false,
        ef: null,
        contractId: '',
        contractUrl: '',
        volumesCovered: 0,
        period: ''
      },
      residualMix: {
        enabled: false,
        ef: null,
        volume: 0
      }
    }
  ], []);

  // Initialize with sample data if empty
  useEffect(() => {
    if (!formData.sites || formData.sites.length === 0) {
      setFormData(prev => ({
        ...prev,
        sites: sampleSites
      }));
    }
  }, [formData.sites, sampleSites]);



  const handleMarketBasedToggle = () => {
    setFormData(prev => ({
      ...prev,
      useMarketBased: !prev.useMarketBased
    }));
  };

  const handleSupplierSpecificToggle = (siteId) => {
    setFormData(prev => ({
      ...prev,
      sites: prev.sites.map(site => 
        site.id === siteId 
          ? { 
              ...site, 
              supplierSpecific: { 
                ...site.supplierSpecific, 
                enabled: !site.supplierSpecific.enabled 
              },
              residualMix: {
                ...site.residualMix,
                enabled: site.supplierSpecific.enabled
              }
            }
          : site
      )
    }));
  };

  const handleSupplierEvidenceChange = (siteId, field, value) => {
    setFormData(prev => ({
      ...prev,
      sites: prev.sites.map(site => 
        site.id === siteId 
          ? { 
              ...site, 
              supplierSpecific: { 
                ...site.supplierSpecific, 
                [field]: value 
              }
            }
          : site
      )
    }));
  };





  const totalLBEmissions = formData.sites.reduce((sum, site) => sum + site.locationBasedEmissions, 0);
  const totalMBEmissions = formData.sites.reduce((sum, site) => sum + site.marketBasedEmissions, 0);

  const getErrorClass = (siteId) => {
    return errors[siteId] ? 'border-red-500 bg-red-50' : '';
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="flex items-center mb-6">
        <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4">
          <Zap className="w-6 h-6 text-yellow-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{t('workflow.calculation.scope2.title')}</h2>
          <p className="text-gray-600">{t('workflow.calculation.scope2.description')}</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Method Toggle */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-blue-900">{t('workflow.calculation.scope2.methodToggle.title')}</h3>
              <p className="text-blue-700">
                {formData.useMarketBased 
                  ? t('workflow.calculation.scope2.methodToggle.enabled')
                  : t('workflow.calculation.scope2.methodToggle.disabled')
                }
              </p>
            </div>
            <button
              type="button"
              onClick={handleMarketBasedToggle}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                formData.useMarketBased
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-600 text-white'
              }`}
            >
              {formData.useMarketBased ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              <span>{formData.useMarketBased ? t('workflow.calculation.scope2.methodToggle.enabledButton') : t('workflow.calculation.scope2.methodToggle.disabledButton')}</span>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900">{t('workflow.calculation.scope2.summary.locationBased.title')}</h3>
            <p className="text-2xl font-bold text-gray-700">{totalLBEmissions.toFixed(1)} tCO₂e</p>
            <p className="text-sm text-gray-600">{t('workflow.calculation.scope2.summary.locationBased.description')}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-green-900">{t('workflow.calculation.scope2.summary.marketBased.title')}</h3>
            <p className="text-2xl font-bold text-green-700">
              {formData.useMarketBased ? `${totalMBEmissions.toFixed(1)} tCO₂e` : 'N/A'}
            </p>
            <p className="text-sm text-green-600">
              {formData.useMarketBased ? t('workflow.calculation.scope2.summary.marketBased.description') : t('workflow.calculation.scope2.summary.marketBased.disabledDescription')}
            </p>
          </div>
        </div>

        {/* Emissions Table */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('workflow.calculation.scope2.table.title')}</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope2.table.site')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope2.table.location')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope2.table.consumption')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope2.table.lbEf')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope2.table.lbTotal')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope2.table.mbEf')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope2.table.mbTotal')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope2.table.method')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">{t('workflow.calculation.scope2.table.status')}</th>
                </tr>
              </thead>
              <tbody>
                {formData.sites.map((site) => (
                  <tr key={site.id} className={`border-b ${getErrorClass(site.id)}`}>
                    <td className="py-3 px-4 font-medium text-gray-900">{site.name}</td>
                    <td className="py-3 px-4 text-gray-700">{site.region}, {site.country}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-1">
                        <span className="text-gray-700">{site.consumption.toLocaleString()}</span>
                        <span className="text-gray-500 text-sm">{site.unit}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-700">{site.locationBasedEF} kg CO₂e/kWh</td>
                    <td className="py-3 px-4 font-semibold text-gray-900">{site.locationBasedEmissions.toFixed(1)}</td>
                    <td className="py-3 px-4 text-gray-700">
                      {formData.useMarketBased ? `${site.marketBasedEF} kg CO₂e/kWh` : 'N/A'}
                    </td>
                    <td className="py-3 px-4 font-semibold text-gray-900">
                      {formData.useMarketBased ? site.marketBasedEmissions.toFixed(1) : 'N/A'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col space-y-1">
                        {formData.useMarketBased ? (
                          <>
                            {site.supplierSpecific.enabled && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">{t('workflow.calculation.scope2.table.supplierSpecific')}</span>
                            )}
                            {site.residualMix.enabled && (
                              <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">{t('workflow.calculation.scope2.table.residualMix')}</span>
                            )}
                            {!site.supplierSpecific.enabled && !site.residualMix.enabled && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{t('workflow.calculation.scope2.table.na')}</span>
                            )}
                          </>
                        ) : (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">N/A</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-1">
                        {errors[site.id] && (
                          <AlertTriangle className="w-4 h-4 text-red-500" title={errors[site.id]} />
                        )}
                        {warnings[site.id] && (
                          <Info className="w-4 h-4 text-yellow-500" title={warnings[site.id]} />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Market-based Evidence */}
        {formData.useMarketBased && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-green-900 mb-4">{t('workflow.calculation.scope2.marketBasedEvidence.title')}</h3>
            
            <div className="space-y-4">
              {formData.sites.map((site) => (
                <div key={site.id} className="bg-white rounded-lg p-4 border border-green-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">{site.name}</h4>
                    <button
                      type="button"
                      onClick={() => handleSupplierSpecificToggle(site.id)}
                      className={`flex items-center space-x-2 px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        site.supplierSpecific.enabled
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-300 text-gray-700'
                      }`}
                    >
                      {site.supplierSpecific.enabled ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                      <span>{t('workflow.calculation.scope2.marketBasedEvidence.supplierSpecific')}</span>
                    </button>
                  </div>

                  {site.supplierSpecific.enabled ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('workflow.calculation.scope2.marketBasedEvidence.contractId')}</label>
                        <input
                          type="text"
                          value={site.supplierSpecific.contractId}
                          onChange={(e) => handleSupplierEvidenceChange(site.id, 'contractId', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                            errors[`${site.id}_contract`] ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder={t('workflow.calculation.scope2.marketBasedEvidence.contractIdPlaceholder')}
                        />
                        {errors[`${site.id}_contract`] && (
                          <p className="text-sm text-red-600 mt-1">{errors[`${site.id}_contract`]}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('workflow.calculation.scope2.marketBasedEvidence.contractUrl')}</label>
                        <input
                          type="url"
                          value={site.supplierSpecific.contractUrl}
                          onChange={(e) => handleSupplierEvidenceChange(site.id, 'contractUrl', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder={t('workflow.calculation.scope2.marketBasedEvidence.contractUrlPlaceholder')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('workflow.calculation.scope2.marketBasedEvidence.volumesCovered')}</label>
                        <input
                          type="number"
                          value={site.supplierSpecific.volumesCovered}
                          onChange={(e) => handleSupplierEvidenceChange(site.id, 'volumesCovered', parseInt(e.target.value) || 0)}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                            errors[`${site.id}_volume`] ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder={t('workflow.calculation.scope2.marketBasedEvidence.volumesCoveredPlaceholder')}
                        />
                        {errors[`${site.id}_volume`] && (
                          <p className="text-sm text-red-600 mt-1">{errors[`${site.id}_volume`]}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('workflow.calculation.scope2.marketBasedEvidence.period')}</label>
                        <input
                          type="text"
                          value={site.supplierSpecific.period}
                          onChange={(e) => handleSupplierEvidenceChange(site.id, 'period', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder={t('workflow.calculation.scope2.marketBasedEvidence.periodPlaceholder')}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-600 text-sm">
                      {site.residualMix.enabled ? (
                        <div className="flex items-center space-x-2">
                          <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs">Residual mix</span>
                          <span>EF: {site.residualMix.ef} kg CO₂e/kWh</span>
                        </div>
                      ) : (
                        <span className="text-gray-500">{t('workflow.calculation.scope2.marketBasedEvidence.noMethodSelected')}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Validation Messages */}
        {Object.keys(errors).length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="text-red-800 font-medium mb-2">Errors</h4>
            <ul className="space-y-1">
              {Object.values(errors).map((error, index) => (
                <li key={index} className="text-red-700 text-sm flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {Object.keys(warnings).length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="text-yellow-800 font-medium mb-2">Warnings</h4>
            <ul className="space-y-1">
              {Object.values(warnings).map((warning, index) => (
                <li key={index} className="text-yellow-700 text-sm flex items-center">
                  <Info className="w-4 h-4 mr-2" />
                  {warning}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Methodology Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-800 mb-2">{t('workflow.calculation.scope2.methodology.title')}</h4>
          <div className="text-sm text-gray-600 space-y-1">
            <p><strong>Location-based:</strong> {t('workflow.calculation.scope2.methodology.locationBased')}</p>
            <p><strong>Market-based:</strong> {t('workflow.calculation.scope2.methodology.marketBased')}</p>
            <p><strong>Both methods:</strong> {t('workflow.calculation.scope2.methodology.bothMethods')}</p>
          </div>
        </div>

        {/* Navigation handled by WorkflowTabView */}
      </div>
    </div>
  );
};

export default Scope2;
